import { createStore as createIdb, del, entries, get, set } from "idb-keyval";

/**
 * Anonymous work: configurations kept in the browser until the user signs in,
 * at which point the shell adopts them into the profile.
 */
export interface Draft {
  id: string;
  name: string;
  designId: string;
  version: string;
  params: Record<string, unknown>;
  brand: string;
  createdAt: string;
  updatedAt: string;
  /** Set once the draft has been uploaded to a profile. */
  adoptedAs?: string;
}

const store = typeof indexedDB !== "undefined" ? createIdb("cfg-drafts", "drafts") : null;
const memory = new Map<string, Draft>();

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export async function listDrafts(brand: string): Promise<Draft[]> {
  const all = store ? (await entries<string, Draft>(store)).map(([, v]) => v) : [...memory.values()];
  return all.filter((d) => d.brand === brand && !d.adoptedAs).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function saveDraft(input: Omit<Draft, "id" | "createdAt" | "updatedAt"> & { id?: string }): Promise<Draft> {
  const now = new Date().toISOString();
  const existing = input.id ? await getDraft(input.id) : undefined;
  const draft: Draft = {
    ...input,
    id: input.id ?? newId(),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
  if (store) await set(draft.id, draft, store);
  else memory.set(draft.id, draft);
  return draft;
}

export async function getDraft(id: string): Promise<Draft | undefined> {
  return store ? get<Draft>(id, store) : memory.get(id);
}

export async function deleteDraft(id: string): Promise<void> {
  if (store) await del(id, store);
  else memory.delete(id);
}

export async function markAdopted(id: string, configId: string): Promise<void> {
  const d = await getDraft(id);
  if (!d) return;
  await saveDraft({ ...d, adoptedAs: configId });
}
